export interface BlockMetadata {
  [key: string]: string[];
}

export interface ParsedBlock {
  id: string;
  content: string;
  metadata?: BlockMetadata;
}

// Matches: <!-- block:id --> or <!-- block:id key=[val1,val2] key2=[val3] -->
const BLOCK_MARKER_PATTERN =
  /<!--\s*block:(\S+)((?:\s+\w+=\[[^\]]*\])*)\s*-->/g;

/**
 * Parse inline metadata from a block marker.
 * Format: key=[val1,val2] key2=[val3]
 */
function parseInlineMetadata(metaString: string): BlockMetadata | undefined {
  if (!metaString?.trim()) return undefined;

  const metadata: BlockMetadata = {};
  const metaPattern = /(\w+)=\[([^\]]*)\]/g;

  for (const match of metaString.matchAll(metaPattern)) {
    const key = match[1];
    const values = match[2]
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    if (values.length > 0) {
      metadata[key] = values;
    }
  }

  return Object.keys(metadata).length > 0 ? metadata : undefined;
}

/**
 * Parse markdown into blocks delimited by <!-- block:id --> markers.
 * Supports inline metadata: <!-- block:id permissions=[a,b] userTypes=[STAFF] -->
 */
export function parseBlocks(markdown: string): ParsedBlock[] {
  if (!markdown) return [];

  const markers: Array<{
    id: string;
    index: number;
    metadata?: BlockMetadata;
  }> = [];
  for (const match of markdown.matchAll(BLOCK_MARKER_PATTERN)) {
    markers.push({
      id: match[1],
      index: match.index!,
      metadata: parseInlineMetadata(match[2]),
    });
  }

  if (markers.length === 0) {
    return [{ id: '_default', content: markdown }];
  }

  const blocks: ParsedBlock[] = [];

  const preContent = markdown.slice(0, markers[0].index).trim();
  if (preContent) {
    blocks.push({ id: '_pre', content: preContent });
  }

  for (let i = 0; i < markers.length; i++) {
    const marker = markers[i];
    const markerEnd = markdown.indexOf('-->', marker.index) + 3;
    const nextMarkerStart =
      i + 1 < markers.length ? markers[i + 1].index : markdown.length;
    const content = markdown.slice(markerEnd, nextMarkerStart).trim();
    blocks.push({ id: marker.id, content, metadata: marker.metadata });
  }

  return blocks;
}

/**
 * Extract all block IDs from markdown content.
 */
export function extractBlockIds(markdown: string): string[] {
  if (!markdown) return [];
  return [...markdown.matchAll(BLOCK_MARKER_PATTERN)].map((match) => match[1]);
}

/**
 * Format inline metadata for a block marker comment.
 */
function formatInlineMetadata(metadata?: BlockMetadata): string {
  if (!metadata) return '';
  return Object.entries(metadata)
    .map(([key, values]) => ` ${key}=[${values.join(',')}]`)
    .join('');
}

/**
 * Reassemble blocks back into markdown with markers and inline metadata.
 */
export function assembleBlocks(blocks: ParsedBlock[]): string {
  return blocks
    .map((block) => {
      if (block.id === '_default' || block.id === '_pre') {
        return block.content;
      }
      const meta = formatInlineMetadata(block.metadata);
      return `<!-- block:${block.id}${meta} -->\n\n${block.content}`;
    })
    .join('\n\n');
}

/**
 * Swap two blocks by index.
 */
export function swapBlocks(
  blocks: ParsedBlock[],
  indexA: number,
  indexB: number,
): ParsedBlock[] {
  if (
    indexA < 0 ||
    indexB < 0 ||
    indexA >= blocks.length ||
    indexB >= blocks.length
  ) {
    return blocks;
  }
  const result = [...blocks];
  const temp = result[indexA];
  result[indexA] = result[indexB];
  result[indexB] = temp;
  return result;
}

/**
 * Generate a slug from text.
 */
export function slugifyBlockId(text: string): string {
  return (
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 50) || '_block'
  );
}
